import { computed, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import type {
  FeatureHighlight,
  FeatureHighlightId,
  HighlightDefinition,
} from "@/model/featureHighlight";
import { defaultHighlightDays, featureHighlights } from "@/model/featureHighlight";
import { useClientSettingsStore } from "@/store/clientSettings";

/**
 * A highlight shows until its item is used, until it was shown on `defaultHighlightDays` different days, or until its `until` date.
 * Counting days the app was open, not calendar days, keeps it for users who only open the app for a PI planning.
 */
export function useFeatureHighlight(highlight: () => FeatureHighlightId | undefined) {
  const settings = useClientSettingsStore();
  const { t } = useI18n();

  const active = computed(() => {
    const current = highlight();
    if (!current || Date.now() >= new Date(`${featureHighlights[current].until}T00:00`).getTime()) {
      return false;
    }

    const seen = settings.seenHighlights[current];
    if (!seen) return true;
    const today = new Date().toDateString();
    const earlierDays = seen.days.filter((day) => day !== today).length;
    return !seen.used && earlierDays < defaultHighlightDays;
  });

  watchEffect(() => {
    if (active.value) settings.recordHighlightDay(highlight()!, new Date().toDateString());
  });

  const shown = computed((): FeatureHighlight | undefined => {
    if (!active.value) return undefined;
    const id = highlight()!;
    const definition: HighlightDefinition = featureHighlights[id];
    return { id, description: t(definition.description), learnMore: definition.learnMore };
  });

  function withBadgeText(label: string) {
    return shown.value ? `${label}, ${t("featureHighlight.badge")}` : label;
  }

  function markUsed() {
    const current = highlight();
    if (current) settings.markHighlightUsed(current);
  }

  return { shown, withBadgeText, markUsed };
}
